import "./App.css";
import React from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useEffect } from "react";
import Navbar from "./components/layout/Navbar";
import Footer from "./components/layout/Footer";
import CookieConsent from "./components/layout/CookieConsent";
import { Toaster } from "./components/ui/sonner";
import Home from "./pages/Home";
import HowItWorks from "./pages/HowItWorks";
import Platform from "./pages/Platform";
import Logberg from "./pages/Logberg";
import Skjold from "./pages/Skjold";
import Vor from "./pages/Vor";
import Defence from "./pages/Defence";
import CaseStudies from "./pages/CaseStudies";
import EnterpriseAI from "./pages/EnterpriseAI";
import RegulatedIndustries from "./pages/RegulatedIndustries";
import TrustCentre from "./pages/TrustCentre";
import FaqPage from "./pages/FaqPage";
import FinancialServices from "./pages/FinancialServices";
import SecurityGovernance from "./pages/SecurityGovernance";
import Insights from "./pages/Insights";
import InsightDetail from "./pages/InsightDetail";
import Company from "./pages/Company";
import BookDemo from "./pages/BookDemo";
import InteractiveDemo from "./pages/InteractiveDemo";
import { DemoProvider } from "./demo/DemoContext";
import { AuthProvider } from "./auth/AuthContext";
import { ContentProvider } from "./cms/ContentContext";
import AuthCallback from "./auth/AuthCallback";
import ProtectedRoute from "./auth/ProtectedRoute";
import InvestorAccess from "./pages/InvestorAccess";
import InvestorDashboard from "./pages/InvestorDashboard";
import DataRoom from "./pages/DataRoom";
import AdminDashboard from "./pages/AdminDashboard";
import SiteEditor from "./pages/SiteEditor";
import GovernanceTools from "./pages/GovernanceTools";
import RoiCalculatorPage from "./pages/RoiCalculator";
import SharedReport from "./pages/SharedReport";
import { GovProvider } from "./governance/GovContext";
import { ThemeProvider } from "./theme/ThemeContext";
import { initAnalytics, trackPageView } from "./lib/analytics";
import { RouteMeta } from "./lib/pageMeta";
import StickyMobileCta from "./components/layout/StickyMobileCta";
import NotFound from "./pages/NotFound";
import ThankYou from "./pages/ThankYou";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" in window ? "instant" : "auto" });
  }, [pathname]);
  return null;
}

function AnalyticsInit() {
  const { pathname } = useLocation();
  useEffect(() => {
    initAnalytics();
    window.addEventListener("norse3:consent", initAnalytics);
    return () => window.removeEventListener("norse3:consent", initAnalytics);
  }, []);
  useEffect(() => { const t = setTimeout(() => trackPageView(pathname), 50); return () => clearTimeout(t); }, [pathname]);
  return null;
}

function AppRoutes() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <>
      <ScrollToTop />
      <AnalyticsInit />
      <Navbar />
      <main>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/how-it-works" element={<HowItWorks />} />
          <Route path="/platform" element={<Platform />} />
          <Route path="/logberg" element={<Logberg />} />
          <Route path="/skjold" element={<Skjold />} />
          <Route path="/vor" element={<Vor />} />
          <Route path="/defence" element={<Defence />} />
          <Route path="/case-studies" element={<CaseStudies />} />
          <Route path="/enterprise-ai" element={<EnterpriseAI />} />
          <Route path="/regulated-industries" element={<RegulatedIndustries />} />
          <Route path="/trust-centre" element={<TrustCentre />} />
          <Route path="/faq" element={<FaqPage />} />
          <Route path="/financial-services" element={<FinancialServices />} />
          <Route path="/security-governance" element={<SecurityGovernance />} />
          <Route path="/insights" element={<Insights />} />
          <Route path="/insights/:slug" element={<InsightDetail />} />
          <Route path="/company" element={<Company />} />
          <Route path="/book-a-demo" element={<BookDemo />} />
          <Route path="/interactive-demo" element={<InteractiveDemo />} />
          <Route path="/governance-tools" element={<GovernanceTools />} />
          <Route path="/roi-calculator" element={<RoiCalculatorPage />} />
          <Route path="/r/:token" element={<SharedReport />} />
          <Route path="/investor" element={<InvestorAccess />} />
          <Route path="/investor/dashboard" element={<ProtectedRoute><InvestorDashboard /></ProtectedRoute>} />
          <Route path="/investor/data-room" element={<ProtectedRoute><DataRoom /></ProtectedRoute>} />
          <Route path="/admin" element={<ProtectedRoute adminOnly><AdminDashboard /></ProtectedRoute>} />
          <Route path="/admin/editor" element={<ProtectedRoute adminOnly><SiteEditor /></ProtectedRoute>} />
          <Route path="/thank-you" element={<ThankYou />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>
      <RouteMeta />
      <StickyMobileCta />
      <Footer />
      <CookieConsent />
    </>
  );
}

function App() {
  return (
    <div className="App">
      <ThemeProvider>
        <AuthProvider>
          <ContentProvider>
            <BrowserRouter>
              <DemoProvider>
                <GovProvider>
                  <AppRoutes />
                  <Toaster position="top-right" theme="system" richColors />
                </GovProvider>
              </DemoProvider>
            </BrowserRouter>
          </ContentProvider>
        </AuthProvider>
      </ThemeProvider>
    </div>
  );
}

export default App;
